import type { ExpenseCategory } from '../../../api/schemas';
import { CATEGORY_LABEL } from '../../../lib/expenses';
import { changeAgainst, formatChange } from '../../../lib/reports';
import type { LineId } from '../voice/lines';
import { periodsFor } from './facts';
import {
  biggest,
  count,
  failure,
  hasMoved,
  incomeFigures,
  labelOf,
  mercurio,
  money,
  onPnl,
  onReport,
  type Reply,
} from './replyKit';

/** The biggest expense of the month, said out loud by its category. */
const BIGGEST_EXPENSE: Readonly<Record<ExpenseCategory, LineId>> = {
  alquiler: 'mayor-alquiler',
  planilla: 'mayor-planilla',
  insumos: 'mayor-insumos',
  servicios: 'mayor-servicios',
  afiliacion_dale: 'mayor-afiliacion',
  otro: 'mayor-otro',
};

const TOP_EXPENSES = 4;
/** A move smaller than this reads as "igual", like the Reportes screen. */
const LEVEL_PERCENT = 0.5;

export const ventas: Reply = onReport('mes', (report) => {
  const totals = report.sales;
  if (!hasMoved(totals)) return mercurio('ventas-nada');
  return mercurio(totals.revenueCents > 0 ? 'ventas-mes' : 'ventas-nada', {
    note: labelOf(report),
    figures: incomeFigures(totals),
  });
});

export const resumen: Reply = onReport('mes', (report) => {
  const totals = report.sales;
  if (!hasMoved(totals)) return mercurio('resultado-nada');
  return mercurio('resumen-mes', { note: labelOf(report), figures: incomeFigures(totals) });
});

/** The wish to improve the finances: the month beside a line that says where to start. */
export const mejorar: Reply = onReport('mes', (report) => {
  const totals = report.sales;
  if (!hasMoved(totals)) return mercurio('resultado-nada');
  return mercurio('mejorar-finanzas', { note: labelOf(report), figures: incomeFigures(totals) });
});

export const hoy: Reply = onReport('hoy', (report) => {
  const totals = report.sales;
  if (totals.revenueCents <= 0) return mercurio('hoy-nada');
  const figures = [money('Ingresos de hoy', totals.revenueCents)];
  if (totals.chargeCount > 0) figures.push(count('Cobros', totals.chargeCount));
  return mercurio('hoy-cobrado', { note: labelOf(report), figures });
});

export const ayer: Reply = onReport('ayer', (report) => {
  const totals = report.sales;
  if (totals.revenueCents <= 0) return mercurio('ayer-nada');
  const figures = [money('Ingresos de ayer', totals.revenueCents)];
  if (totals.chargeCount > 0) figures.push(count('Cobros', totals.chargeCount));
  return mercurio('ayer-cobrado', { note: labelOf(report), figures });
});

export const semana: Reply = onReport('semana', (report) => {
  const totals = report.sales;
  if (!hasMoved(totals)) return mercurio('semana-nada');
  return mercurio(totals.revenueCents > 0 ? 'semana-cobrado' : 'semana-nada', { note: labelOf(report), figures: incomeFigures(totals) });
});

export const resultado: Reply = onReport('mes', (report, { board }) => {
  const totals = report.sales;
  if (!hasMoved(totals)) return mercurio('resultado-nada');
  if (totals.expenseCents <= 0) {
    const task = board.agents.find((agent) => agent.id === 'finanzas')?.task;
    return mercurio('resultado-sin-gastos', { note: task ?? labelOf(report), figures: incomeFigures(totals) });
  }
  const voz = totals.resultCents >= 0 ? 'resultado-positivo' : 'resultado-negativo';
  return mercurio(voz, { note: labelOf(report), figures: incomeFigures(totals) });
});

export const gastos: Reply = onReport('mes', (report) => {
  const total = report.sales.expenseCents;
  if (total <= 0) return mercurio('gastos-nada');
  return mercurio('gastos-mes', { note: labelOf(report), figures: [money('Gastos', total)] });
});

export const mayorGasto: Reply = onPnl((pnl) => {
  const entries = Object.entries(pnl.expensesByCategory) as [ExpenseCategory, number][];
  const ranked = biggest(entries, ([, cents]) => cents, TOP_EXPENSES);
  const top = ranked[0];
  if (!top) return mercurio('gastos-nada');
  return mercurio(BIGGEST_EXPENSE[top[0]], {
    note: labelOf(pnl),
    figures: ranked.map(([category, cents]) => money(CATEGORY_LABEL[category], cents)),
  });
});

export const planilla: Reply = onPnl((pnl) => {
  const cents = pnl.expensesByCategory.planilla ?? 0;
  if (cents <= 0) return mercurio('planilla-nada');
  return mercurio('planilla-mes', { note: labelOf(pnl), figures: [money('Planilla', cents)] });
});

export const gastosFijos: Reply = ({ facts }) => {
  if (!facts.expenses) return failure();
  const today = periodsFor(facts.now).hoy.from;
  const running = facts.expenses.filter(
    (expense) => expense.isActive && expense.isRecurringMonthly && (!expense.recurringUntil || expense.recurringUntil >= today),
  );
  if (running.length === 0) return mercurio('fijos-nada');
  const total = running.reduce((sum, expense) => sum + expense.amountCents, 0);
  return mercurio('fijos-hay', { figures: [money('Gastos fijos al mes', total), count('Gastos', running.length)] });
};

/** This month so far against the same days of last month, so a young month is not measured against a whole one. */
export const comparar: Reply = ({ facts }) => {
  const now = facts.reports.mes;
  const before = facts.reports['mismos-dias'];
  if (!now || !before) return failure();
  if (before.sales.revenueCents <= 0) return mercurio('comparar-nada');
  const change = changeAgainst(now.sales.revenueCents, before.sales.revenueCents) ?? 0;
  const level = Math.abs(change) < LEVEL_PERCENT;
  const voz = level ? 'comparar-igual' : change > 0 ? 'comparar-sube' : 'comparar-baja';
  const shift = { label: 'Cambio', value: formatChange(change) ?? 'igual', ...(change < 0 && !level ? { tone: 'negative' as const } : {}) };
  return mercurio(voz, {
    note: `${labelOf(now)} contra ${labelOf(before)}`,
    figures: [money('Este mes', now.sales.revenueCents), money('Mes pasado, mismos días', before.sales.revenueCents), shift],
  });
};

export const mesPasado: Reply = onReport('mes-pasado', (report) => {
  if (!hasMoved(report.sales)) return mercurio('mes-pasado-nada');
  return mercurio('mes-pasado', { note: labelOf(report), figures: incomeFigures(report.sales) });
});

export const anio: Reply = onReport('anio', (report) => {
  if (!hasMoved(report.sales)) return mercurio('anio-nada');
  return mercurio('anio', { note: labelOf(report), figures: incomeFigures(report.sales) });
});
